import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './CatalogBrowser.css';

const CatalogBrowser = ({ onResourceSelect, selectedResources = {} }) => {
  const [activeCategory, setActiveCategory] = useState('psychometrics');
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const categories = [
    { key: 'psychometrics', label: 'Psychometrics', icon: '🧠' },
    { key: 'frameworks', label: 'Frameworks', icon: '📋' },
    { key: 'tools', label: 'Tools', icon: '🛠️' },
    { key: 'games', label: 'Games', icon: '🎮' }
  ];

  useEffect(() => {
    loadItems();
  }, [activeCategory, page, searchQuery]);

  const loadItems = async () => {
    setLoading(true);
    try {
      let response;
      if (searchQuery.trim()) {
        response = await apiService.searchCatalogs(searchQuery, activeCategory, page);
      } else {
        response = await apiService.getCatalogItems(activeCategory, page);
      }
      
      setItems(response.items || []);
      setTotalPages(response.total_pages || 1);
    } catch (error) {
      console.error('Error loading catalog items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const handleSelectItem = (item) => {
    if (onResourceSelect) {
      onResourceSelect(item);
    }
    setShowDetailModal(false);
  };

  const isItemSelected = (item) => {
    const categoryItems = selectedResources[activeCategory] || [];
    return categoryItems.some(selected => selected.id === item.id);
  };

  const getCategoryCount = () => {
    const categoryItems = selectedResources[activeCategory] || [];
    return categoryItems.length;
  };

  const renderItemCard = (item) => (
    <div 
      key={item.id} 
      className={`catalog-item-card ${isItemSelected(item) ? 'selected' : ''}`}
      onClick={() => handleItemClick(item)}
    >
      <div className="item-header">
        <h3 className="item-name">{item.name}</h3>
        {isItemSelected(item) && <span className="selected-badge">✓ Selected</span>}
      </div>
      
      <div className="item-preview">
        <p className="item-what">{item.what}</p>
        <p className="item-why">{item.why}</p>
      </div>
      
      <div className="item-meta">
        <span className="item-industry">{item.industry}</span>
        {item.duration && <span className="item-duration">{item.duration}</span>}
      </div>
    </div>
  );

  const renderDetailModal = () => {
    if (!selectedItem) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{selectedItem.name}</h2>
            <button 
              className="close-button" 
              onClick={() => setShowDetailModal(false)}
            >
              ✕
            </button>
          </div>
          
          <div className="modal-body">
            <div className="detail-section">
              <h3>What</h3>
              <p>{selectedItem.what}</p>
            </div>
            
            <div className="detail-section">
              <h3>Why</h3>
              <p>{selectedItem.why}</p>
            </div>

            {selectedItem.detailed_explanation && (
              <div className="detail-section">
                <h3>Detailed Explanation</h3>
                <p>{selectedItem.detailed_explanation}</p>
              </div>
            )}
            
            {selectedItem.outcomes && selectedItem.outcomes.length > 0 && (
              <div className="detail-section">
                <h3>Outcomes</h3>
                <ul>
                  {selectedItem.outcomes.map((outcome, index) => (
                    <li key={index}>{outcome}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="detail-section">
              <h3>How to Use</h3>
              <p>{selectedItem.how_to}</p>
            </div>
            
            <div className="detail-section">
              <h3>Problem Solved</h3>
              <p>{selectedItem.problem_solved}</p>
            </div>

            {selectedItem.detailed_explanation && (
              <div className="detail-section">
                <h3>Detailed Explanation</h3>
                <p>{selectedItem.detailed_explanation}</p>
              </div>
            )}

            {selectedItem.materials && selectedItem.materials.length > 0 && (
              <div className="detail-section">
                <h3>Materials</h3>
                <ul>
                  {selectedItem.materials.map((material, index) => (
                    <li key={index}>{material}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="detail-section">
              <h3>Industry</h3>
              <p>{selectedItem.industry}</p>
            </div>
            
            {/* Category-specific details */}
            {activeCategory === 'psychometrics' && (
              <>
                {selectedItem.provider && (
                  <div className="detail-section">
                    <h3>Provider</h3>
                    <p>{selectedItem.provider}</p>
                  </div>
                )}
                {selectedItem.assessment_type && (
                  <div className="detail-section">
                    <h3>Assessment Type</h3>
                    <p>{selectedItem.assessment_type}</p>
                  </div>
                )}
                {selectedItem.duration && (
                  <div className="detail-section">
                    <h3>Duration</h3>
                    <p>{selectedItem.duration}</p>
                  </div>
                )}
              </>
            )}
            
            {activeCategory === 'frameworks' && (
              <>
                {selectedItem.purpose && (
                  <div className="detail-section">
                    <h3>Purpose</h3>
                    <p>{selectedItem.purpose}</p>
                  </div>
                )}
                {selectedItem.components && selectedItem.components.length > 0 && (
                  <div className="detail-section">
                    <h3>Components</h3>
                    <ul>
                      {selectedItem.components.map((component, index) => (
                        <li key={index}>{component}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            
            {activeCategory === 'tools' && (
              <>
                {selectedItem.platform && (
                  <div className="detail-section">
                    <h3>Platform</h3>
                    <p>{selectedItem.platform}</p>
                  </div>
                )}
                {selectedItem.key_features && selectedItem.key_features.length > 0 && (
                  <div className="detail-section">
                    <h3>Key Features</h3>
                    <ul>
                      {selectedItem.key_features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            
            {activeCategory === 'games' && (
              <>
                {selectedItem.duration && (
                  <div className="detail-section">
                    <h3>Duration</h3>
                    <p>{selectedItem.duration}</p>
                  </div>
                )}
                {selectedItem.participants && (
                  <div className="detail-section">
                    <h3>Participants</h3>
                    <p>{selectedItem.participants}</p>
                  </div>
                )}
                {selectedItem.debrief_points && selectedItem.debrief_points.length > 0 && (
                  <div className="detail-section">
                    <h3>Debrief Points</h3>
                    <ul>
                      {selectedItem.debrief_points.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="modal-footer">
            <button 
              className="select-button"
              onClick={() => handleSelectItem(selectedItem)}
            >
              {isItemSelected(selectedItem) ? 'Remove from Selection' : 'Add to Selection'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="catalog-browser">
      <div className="catalog-header">
        <h1>Training Resources Catalog</h1>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>
      
      <div className="catalog-tabs">
        {categories.map(category => (
          <button
            key={category.key}
            className={`tab-button ${activeCategory === category.key ? 'active' : ''}`}
            onClick={() => {
              setActiveCategory(category.key);
              setPage(1);
            }}
          >
            <span className="tab-icon">{category.icon}</span>
            <span className="tab-label">{category.label}</span>
            {getCategoryCount() > 0 && (
              <span className="tab-count">({getCategoryCount()})</span>
            )}
          </button>
        ))}
      </div>
      
      <div className="catalog-content">
        {loading ? (
          <div className="loading">Loading resources...</div>
        ) : (
          <>
            <div className="items-grid">
              {items.map(renderItemCard)}
            </div>
            
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span>Page {page} of {totalPages}</span>
                <button 
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      {showDetailModal && renderDetailModal()}
    </div>
  );
};

export default CatalogBrowser;
